import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import UserProfileContext from '../../context/userprofilecontext.js';
import MBSDisplayOptionsForm from './MBSDisplayOptionsForm.js';
import { AssetsLiabilitiesTable, TotalsTable, getTableHeaders, FinalSettlementTable } from './table-asset-liability.js';
import { useGetCaseDetails } from '../../api/casedetails';
import usePrintMBSPDF from './PDFPrintMBS.js';
import { typography } from '../../design-system/tokens/tokens.js';
import { buildAssignmentLayout, assignmentTotalKey } from '../../utils/accountAssignment';

const MaritalBalanceSheet = ({ initialData }) => {
  const data = initialData;

  const navigate = useNavigate();

  // Display options state
  const [displayOptions, setDisplayOptions] = useState({
    isDraft: true,
    showNotes: true,
    showSourceColumns: true,
    showSettlementColumns: true
  });

  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext?.userProfile || {};
  const currentCase = userProfile.currentCase || {};
  const currentOrg = userProfile.currentOrg || {};
  const { data: caseDetails, error: caseDetailsError, isLoading: caseDetailsIsLoading } = useGetCaseDetails()

  const handlePrint = usePrintMBSPDF(data, displayOptions);

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    const formatted = absValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return value < 0 ? `(${formatted})` : `${formatted}`;
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const calculateAccountNetValue = (account) => {
    let netValue = account.latest_balance.end_balance || 0;
    if (account.associated_liabilities) {
      netValue += account.associated_liabilities.reduce((sum, liab) => sum + (liab.latest_balance.end_balance || 0), 0);
    }
    return netValue;
  };

  const calculateSourceValues = (account) => {
    const end_balance = account.latest_balance.end_balance || 0;
    const petitionerSource = account._petitioner_owning_pct || 0;
    const petitionerValue = account.latest_balance._petitioner_owning_value ?? end_balance * petitionerSource;
    const respondentSource = account._respondent_owning_pct || 0;
    const respondentValue = account.latest_balance._respondent_owning_value ?? end_balance * respondentSource;
    const otherSource = account._stated_other_pct || 0;
    const otherValue = end_balance * otherSource;
    const maritalSource = account._stated_marital_pct ?? (1 - petitionerSource - respondentSource - otherSource);
    const petitionerSettlement = account._petitioner_settlement_pct || 0;
    const maritalValue = end_balance * maritalSource;
    const petitionerSettlementValue = account.latest_balance._petitioner_settlement_value ?? maritalValue * petitionerSettlement;
    const respondentSettlementValue = maritalValue - petitionerSettlementValue;

    return {
      petitionerValue: petitionerValue,
      respondentValue: respondentValue,
      otherValue,
      maritalValue,
      petitionerSource,
      respondentSource,
      otherSource,
      maritalSource,
      petitionerSettlement,
      respondentSettlement: 1 - petitionerSettlement,
      petitionerSettlementValue,
      respondentSettlementValue
    };
  };

  // Calculate equity source values for accounts with associated liabilities
  const calculateEquitySourceValues = (account) => {
    const accountSourceValues = calculateSourceValues(account);
    let totalPetitionerValue = accountSourceValues.petitionerValue;
    let totalRespondentValue = accountSourceValues.respondentValue;
    let totalMaritalValue = accountSourceValues.maritalValue;
    let totalPetitionerSettlementValue = accountSourceValues.petitionerSettlementValue;
    let totalRespondentSettlementValue = accountSourceValues.respondentSettlementValue;

    if (account.associated_liabilities) {
      account.associated_liabilities.forEach(liability => {
        // Apply parent account percentages to liability
        const liabilityValue = liability.latest_balance.end_balance || 0;
        totalPetitionerValue += liabilityValue * accountSourceValues.petitionerSource;
        totalRespondentValue += liabilityValue * accountSourceValues.respondentSource;
        const incrementalMaritalValue = liabilityValue * accountSourceValues.maritalSource;
        totalMaritalValue += incrementalMaritalValue;
        const incrementalPetitionerSettlement = incrementalMaritalValue * accountSourceValues.petitionerSettlement;
        totalPetitionerSettlementValue += incrementalPetitionerSettlement;
        const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
        totalRespondentSettlementValue += incrementalRespondentSettlement;
      });
    }

    return {
      petitionerValue: totalPetitionerValue,
      respondentValue: totalRespondentValue,
      maritalValue: totalMaritalValue,
      petitionerSettlementValue: totalPetitionerSettlementValue,
      respondentSettlementValue: totalRespondentSettlementValue
    };
  };

  const calculateSectionTotal = (section) => {
    const accounts = section.accounts || [];
    return accounts.reduce((sum, account) => {
      return sum + calculateAccountNetValue(account);
    }, 0);
  };

  const calculateSectionSourceTotals = (section) => {
    const accounts = section.accounts || [];
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let otherTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    accounts.forEach(account => {
      const sourceValues = calculateSourceValues(account);
      petitionerTotal += sourceValues.petitionerValue;
      respondentTotal += sourceValues.respondentValue;
      otherTotal += sourceValues.otherValue;
      maritalTotal += sourceValues.maritalValue;
      petitionerSettlementTotal += sourceValues.petitionerSettlementValue;
      respondentSettlementTotal += sourceValues.respondentSettlementValue;

      // Add associated liabilities
      if (account.associated_liabilities) {
        account.associated_liabilities.forEach(liability => {
          // Use parent account percentages for liabilities
          const liabilityValue = liability.latest_balance.end_balance || 0;
          petitionerTotal += liabilityValue * sourceValues.petitionerSource;
          respondentTotal += liabilityValue * sourceValues.respondentSource;
          otherTotal += liabilityValue * sourceValues.otherSource;
          const incrementalMaritalValue = liabilityValue * sourceValues.maritalSource;
          maritalTotal += incrementalMaritalValue;
          const incrementalPetitionerSettlement = incrementalMaritalValue * sourceValues.petitionerSettlement;
          petitionerSettlementTotal += incrementalPetitionerSettlement;
          const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
          respondentSettlementTotal += incrementalRespondentSettlement;
        });
      }
    });

    return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
  };

  const calculateCategoryTotal = (category) => {
    return Object.entries(data[category]).reduce((sum, [_, section]) => {
      return sum + calculateSectionTotal(section);
    }, 0);
  };

  const calculateCategorySourceTotals = (category) => {
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let otherTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    Object.entries(data[category]).forEach(([_, section]) => {
      const sourceTotals = calculateSectionSourceTotals(section);
      petitionerTotal += sourceTotals.petitionerTotal;
      respondentTotal += sourceTotals.respondentTotal;
      otherTotal += sourceTotals.otherTotal;
      maritalTotal += sourceTotals.maritalTotal;
      petitionerSettlementTotal += sourceTotals.petitionerSettlementTotal;
      respondentSettlementTotal += sourceTotals.respondentSettlementTotal;
    });

    return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
  };

  // Generate line numbers in on-screen order. Accounts are laid out by
  // assignment group (Personal, then each business / other entity) when any
  // account is entity-titled; keys match AssetsLiabilitiesTable's.
  const generateLineNumbers = (data) => {
    let lineNumber = 1;
    const lineNumbers = {};

    const numberSide = (category) => {
      buildAssignmentLayout(data[category] || {}).forEach(({ assignment, keyPrefix, sections }) => {
        Object.entries(sections).forEach(([sectionName, section]) => {
          const sectionKey = `${keyPrefix}${sectionName}`;
          (section.accounts || []).forEach((account, index) => {
            const accountKey = `${category}-${sectionKey}-${index}-${account.id}`;
            lineNumbers[accountKey] = lineNumber++;

            // Only add liability and equity lines if there are actually associated liabilities
            if (account.associated_liabilities && account.associated_liabilities.length > 0) {
              account.associated_liabilities.forEach(liability => {
                lineNumbers[`${accountKey}-liability-${liability.id}`] = lineNumber++;
              });
              lineNumbers[`${accountKey}-equity`] = lineNumber++;
            }
          });
          lineNumbers[`${category}-${sectionKey}-total`] = lineNumber++;
        });
        if (assignment) lineNumbers[assignmentTotalKey(category, assignment)] = lineNumber++;
      });
    };

    numberSide('assets');
    lineNumbers['total-assets'] = lineNumber++;

    numberSide('liabilities');
    lineNumbers['total-liabilities'] = lineNumber++;

    lineNumbers['net-worth'] = lineNumber++;

    lineNumbers['final-settlement-adjustment'] = lineNumber++;
    lineNumbers['final-settlement-total'] = lineNumber++;

    return lineNumbers;
  };

  // Generate line numbers once at the component level
  const lineNumbers = generateLineNumbers(data);




  // Show loading skeleton while case details are loading
  if (caseDetailsIsLoading) {
    return (
      <div style={{ padding: '24px', fontFamily: typography.fontFamily.body, maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <Skeleton variant="text" sx={{ fontSize: '32px', width: '350px' }} />
          <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: '4px' }} />
        </div>

        <Skeleton variant="rectangular" height={120} sx={{ mb: 4, borderRadius: '4px' }} />

        <Skeleton variant="text" sx={{ fontSize: '28px', width: '150px', mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ mb: 2, borderRadius: '4px' }} />
        <Skeleton variant="rectangular" height={60} sx={{ mb: 4, borderRadius: '4px' }} />

        <Skeleton variant="text" sx={{ fontSize: '28px', width: '180px', mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ mb: 2, borderRadius: '4px' }} />
        <Skeleton variant="rectangular" height={60} sx={{ mb: 4, borderRadius: '4px' }} />

        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '4px' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '8px', fontFamily: typography.fontFamily.body, maxWidth: '100%', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, fontFamily: typography.fontFamily.display }}>
          Marital Balance Sheet
        </h1>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={handlePrint}
            style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }}
          >
            ️ Print / Save as PDF
          </button>
          {userProfile.canAccess('/manage_accounts') &&
            <button
              onClick={() => navigate('/manage_accounts')}
              style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }}
            >
              ️ Edit
            </button>
          }
        </div>
      </div>


      <MBSDisplayOptionsForm
        displayOptions={displayOptions}
        setDisplayOptions={setDisplayOptions}
      />

      <div>
        <h2 style={{ fontSize: '28px', marginBottom: '24px', fontFamily: typography.fontFamily.display }}>Assets</h2>
        <AssetsLiabilitiesTable
          accountType="assets"
          data={data}
          groupByAssignment
          displayOptions={displayOptions}
          lineNumbers={lineNumbers}
        />
        {(() => {
          const assetSourceTotals = calculateCategorySourceTotals('assets');
          return (
            <TotalsTable
              displayOptions={displayOptions}
              title="Total Assets"
              lineNumber={lineNumbers['total-assets']}
              value={calculateCategoryTotal('assets')}
              petitionerTotal={assetSourceTotals.petitionerTotal}
              respondentTotal={assetSourceTotals.respondentTotal}
              otherTotal={assetSourceTotals.otherTotal}
              maritalTotal={assetSourceTotals.maritalTotal}
              petitionerSettlementTotal={assetSourceTotals.petitionerSettlementTotal}
              respondentSettlementTotal={assetSourceTotals.respondentSettlementTotal}
            />
          );
        })()}
      </div>

      <div style={{ borderTop: '2px solid #ddd', margin: '32px 0' }}></div>

      <div>
        <h2 style={{ fontSize: '28px', marginBottom: '24px', fontFamily: typography.fontFamily.display }}>Liabilities</h2>
        <AssetsLiabilitiesTable
          accountType="liabilities"
          data={data}
          groupByAssignment
          displayOptions={displayOptions}
          lineNumbers={lineNumbers}
        />

        {(() => {
          const liabilitySourceTotals = calculateCategorySourceTotals('liabilities');
          return (
            <TotalsTable
              displayOptions={displayOptions}
              title="Total Liabilities"
              lineNumber={lineNumbers['total-liabilities']}
              value={calculateCategoryTotal('liabilities')}
              petitionerTotal={liabilitySourceTotals.petitionerTotal}
              respondentTotal={liabilitySourceTotals.respondentTotal}
              otherTotal={liabilitySourceTotals.otherTotal}
              maritalTotal={liabilitySourceTotals.maritalTotal}
              petitionerSettlementTotal={liabilitySourceTotals.petitionerSettlementTotal}
              respondentSettlementTotal={liabilitySourceTotals.respondentSettlementTotal}
            />
          );
        })()}
      </div>

      <div style={{ borderTop: '2px solid #ddd', margin: '32px 0' }}></div>

      {(() => {
        const assetSourceTotals = calculateCategorySourceTotals('assets');
        const liabilitySourceTotals = calculateCategorySourceTotals('liabilities');
        const netWorth = calculateCategoryTotal('assets') + calculateCategoryTotal('liabilities');
        const netPetitioner = assetSourceTotals.petitionerTotal + liabilitySourceTotals.petitionerTotal;
        const netRespondent = assetSourceTotals.respondentTotal + liabilitySourceTotals.respondentTotal;
        const netOther = assetSourceTotals.otherTotal + liabilitySourceTotals.otherTotal;
        const netMarital = assetSourceTotals.maritalTotal + liabilitySourceTotals.maritalTotal;
        const netPetitionerSettlement = assetSourceTotals.petitionerSettlementTotal + liabilitySourceTotals.petitionerSettlementTotal;
        const netRespondentSettlement = assetSourceTotals.respondentSettlementTotal + liabilitySourceTotals.respondentSettlementTotal;
        return (
          <TotalsTable
            displayOptions={displayOptions}
            title="Net Worth"
            lineNumber={lineNumbers['net-worth']}
            value={netWorth}
            petitionerTotal={netPetitioner}
            respondentTotal={netRespondent}
            otherTotal={netOther}
            maritalTotal={netMarital}
            petitionerSettlementTotal={netPetitionerSettlement}
            respondentSettlementTotal={netRespondentSettlement}
          />
        );
      })()}

      <div style={{ borderTop: '2px solid #ddd', margin: '32px 0' }}></div>

      {displayOptions.showSettlementColumns &&(() => {
        const assetSourceTotals = calculateCategorySourceTotals('assets');
        const liabilitySourceTotals = calculateCategorySourceTotals('liabilities');
        const netWorth = calculateCategoryTotal('assets') + calculateCategoryTotal('liabilities');
        const netPetitioner = assetSourceTotals.petitionerTotal + liabilitySourceTotals.petitionerTotal;
        const netRespondent = assetSourceTotals.respondentTotal + liabilitySourceTotals.respondentTotal;
        const netOther = assetSourceTotals.otherTotal + liabilitySourceTotals.otherTotal;
        const netMarital = assetSourceTotals.maritalTotal + liabilitySourceTotals.maritalTotal;
        const netPetitionerSettlement = assetSourceTotals.petitionerSettlementTotal + liabilitySourceTotals.petitionerSettlementTotal;
        const netRespondentSettlement = assetSourceTotals.respondentSettlementTotal + liabilitySourceTotals.respondentSettlementTotal;
        // settlement_adjustment
        // {formatCurrency(-(watchedData?.settlement_adjustment?.petitioner_final_adjustment || 0))}
        const settlementAdjustment = initialData?.settlement_adjustment || {};
        return (
          <FinalSettlementTable
            displayOptions={displayOptions}
            title="Final Settlement Amount"
            lineNumbers={lineNumbers}
            value={netWorth}
            petitionerTotal={netPetitioner}
            respondentTotal={netRespondent}
            otherTotal={netOther}
            maritalTotal={netMarital}
            petitionerSettlementTotal={netPetitionerSettlement}
            respondentSettlementTotal={netRespondentSettlement}
            settlementAdjustment={settlementAdjustment}
          />
        );
      })()}
    </div>
  );
};

export default MaritalBalanceSheet;